import './CustomBrandingAccordion.scss';
import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import TagsSection from '../tags-section/TagsSection';
import TextPropertyToolbar from '../text-property-toolbar/TextPropertyToolbar';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TagItem} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {t} from 'i18next';

interface BrandingData {
  brandingText: string;
  brandingLogicId: number;
  selectedTagList: string;
  selectedTags: TagItem[];
  disabledTags?: TagItem[];
  brandingFont: string;
  brandingFontStyle: string;
  brandingFontSize: number;
}

interface CustomBrandingAccordionProps {
  id: string;
  index: number;
  brandingData: BrandingData;
  onDelete: (id: string) => void;
  onBrandingDataChange: (id: string, data: BrandingData) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  fontOptions: ISelectItem[];
  fontStyleOptions: ISelectItem[];
  fontSizeOptions: ISelectItem[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  placeholder?: string;
  disabled?: boolean;
}

const CustomBrandingAccordion: React.FC<CustomBrandingAccordionProps> = ({
  id,
  index,
  brandingData,
  onDelete,
  onBrandingDataChange,
  tagsTreeData = [],
  fontOptions,
  fontStyleOptions,
  fontSizeOptions,
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  placeholder,
  disabled = false,
}) => {
  const handleTagsChange = (tags: TagItem[]) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      selectedTags: tags,
    });
  };

  const handleFontChange = (font: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingFont: font,
    });
  };

  const handleFontStyleChange = (style: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingFontStyle: style,
    });
  };

  const handleFontSizeChange = (size: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingFontSize: Number(size),
    });
  };

  const handleTextChange = (text: string) => {
    if (disabled) return;
    onBrandingDataChange(id, {
      ...brandingData,
      brandingText: text,
    });
  };
  return (
    <Box className="custom-branding-accordion-wrapper">
      <Box className="custom-branding-header-wrapper">
        <Accordion className="custom-branding-accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="custom-branding-accordion-summary"
          >
            <Typography
              className="typo-text subhead4_size custom-branding-title"
              data-testid={`custom-branding-accordion-custom-branding-accordion-header`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.custom.customBrandingAccordionHeader',
                {index: index + 1}
              )}
            </Typography>
          </AccordionSummary>

          <AccordionDetails className="custom-branding-details">
            <Box className="custom-branding-content">
              {/* Tags Section */}
              <TagsSection
                selectedTags={brandingData.selectedTags}
                onTagsChange={handleTagsChange}
                tagsTreeData={tagsTreeData}
                disabledTags={brandingData.disabledTags}
                disabled={disabled}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                dataTestId="branding-text"
                fontFamily={brandingData.brandingFont}
                fontStyle={brandingData.brandingFontStyle}
                fontSize={String(brandingData.brandingFontSize)}
                toolbarText={brandingData.brandingText}
                fontOptions={fontOptions}
                fontStyleOptions={fontStyleOptions}
                fontSizeOptions={fontSizeOptions}
                insertFieldTypes={insertFieldTypes}
                metadataFieldOptions={metadataFieldOptions}
                productionFieldOptions={productionFieldOptions}
                onFontFamilyChange={handleFontChange}
                onFontStyleChange={handleFontStyleChange}
                onFontSizeChange={handleFontSizeChange}
                onToolbarTextChange={handleTextChange}
                onInsertField={(field) => {
                  handleTextChange(
                    `${brandingData.brandingText || ''}$${field}$`
                  );
                }}
                placeholder={placeholder}
                disabled={disabled}
                textAreaTestId={`Enter-branding-text-input-${index}`}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        {!disabled && (
          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => onDelete(id)}
            className="delete-btn"
            dataTestId="custom-branding-accordion-button"
          >
            <DeleteIcon />
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default CustomBrandingAccordion;
